import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle2, Clock, RefreshCw, Users, Video, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function AdminReminders() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reminders/status");
      setStatus(data);
    } catch { toast.error("Failed to load reminder status"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/reminders/run-now");
      const sessions = data?.session?.sent || 0;
      const meetups = data?.meetup?.sent || 0;
      toast.success(`Swept — sent ${sessions} session + ${meetups} meetup reminders.`);
      await load();
    } catch { toast.error("Sweep failed"); }
    finally { setRunning(false); }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-reminders-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Automation</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight">24-Hour Reminders</h1>
          <p className="text-sm text-zinc-400 mt-1">Auto-fires day-before reminders for Zoom sessions + community meetups. Idempotent (each row is reminded exactly once).</p>
        </div>
        <Button onClick={runNow} disabled={running} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs" data-testid="reminders-run-now">
          <RefreshCw className={`h-4 w-4 mr-2 ${running ? "animate-spin" : ""}`} /> {running ? "Sweeping…" : "Run sweep now"}
        </Button>
      </header>

      {loading || !status ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          {/* Status card */}
          <div className="grid sm:grid-cols-3 gap-4">
            <StatusTile icon={Bell} label="Scheduler" value={status.scheduler_running ? "Running" : "Off"} good={status.scheduler_running} />
            <StatusTile icon={Clock} label="Sweep interval" value={`Every ${Math.round(status.tick_seconds / 60)} min`} />
            <StatusTile icon={CheckCircle2} label="Window" value={`${status.window_hours} h before event`} />
          </div>

          {/* Upcoming sessions */}
          <div className="border border-[#27272A] bg-[#0F0F0F]">
            <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
              <Video className="h-4 w-4 text-[#FF331F]" />
              <div className="font-display uppercase tracking-widest text-sm">Upcoming sessions · Next 48h</div>
            </div>
            {status.upcoming_sessions_48h.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No scheduled sessions in the next 48 hours.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
                  <tr><th className="p-3 text-left">Client</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Starts at</th><th className="p-3 text-left">Reminder</th></tr>
                </thead>
                <tbody>
                  {status.upcoming_sessions_48h.map((s) => (
                    <tr key={s.id} className="border-b border-[#27272A]" data-testid={`reminder-session-${s.id}`}>
                      <td className="p-3 text-zinc-200">{s.client_name || "—"}</td>
                      <td className="p-3 text-zinc-400 text-xs">{s.client_email}</td>
                      <td className="p-3 text-zinc-300 text-xs">{fmt(s.starts_at)}</td>
                      <td className="p-3">{s.reminder_sent_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] uppercase tracking-widest font-bold"><CheckCircle2 className="h-3 w-3" /> Sent {fmt(s.reminder_sent_at)}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-widest font-bold"><Clock className="h-3 w-3" /> Pending</span>
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Upcoming meetups */}
          <div className="border border-[#27272A] bg-[#0F0F0F]">
            <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#FF331F]" />
              <div className="font-display uppercase tracking-widest text-sm">Upcoming meetups · Next 48h</div>
            </div>
            {status.upcoming_meetups_48h.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No meetups in the next 48 hours.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
                  <tr><th className="p-3 text-left">Title</th><th className="p-3 text-left">Starts</th><th className="p-3 text-left">RSVPs</th><th className="p-3 text-left">Reminders sent</th></tr>
                </thead>
                <tbody>
                  {status.upcoming_meetups_48h.map((m) => (
                    <tr key={m.id} className="border-b border-[#27272A]" data-testid={`reminder-meetup-${m.id}`}>
                      <td className="p-3 text-zinc-200">{m.title}</td>
                      <td className="p-3 text-zinc-300 text-xs">{fmt(m.starts_at)}</td>
                      <td className="p-3 text-zinc-400"><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {m.rsvp_count}</span></td>
                      <td className="p-3">
                        {m.reminders_sent >= m.rsvp_count && m.rsvp_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] uppercase tracking-widest font-bold"><CheckCircle2 className="h-3 w-3" /> {m.reminders_sent}/{m.rsvp_count} sent</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-widest font-bold"><Clock className="h-3 w-3" /> {m.reminders_sent}/{m.rsvp_count} pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatusTile({ icon: Icon, label, value, good }) {
  return (
    <div className="border border-[#27272A] bg-[#0F0F0F] p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${good ? "text-emerald-400" : "text-[#FF331F]"}`} />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      </div>
      <div className="font-display text-2xl text-white uppercase tracking-tight">{value}</div>
    </div>
  );
}
